<script>
import axios from "axios";

export default {
  name: "ApplicationProgrammeModal",
  props: {
    application: { type: Object, required: true },
    offerings: { type: Array, default: () => [] },
  },
  emits: ["close", "saved"],
  data: () => ({ programOfferingId: "", reason: "", saving: false, error: "" }),
  computed: {
    currentOfferingId() {
      return (
        this.application.programOfferingId?._id ||
        this.application.programOfferingId
      );
    },
    availableOfferings() {
      return this.offerings.filter(
        (item) => item._id !== this.currentOfferingId && item.active !== false
      );
    },
  },
  methods: {
    label(offering) {
      return [
        offering.structure?.type?.description || offering.structure?.type?.type,
        offering.structure?.mode?.description || offering.structure?.mode?.mode,
        offering.program?.name,
      ]
        .filter(Boolean)
        .join(" · ");
    },
    async save() {
      this.error = "";
      if (!this.programOfferingId)
        this.error = "Select a destination programme offering.";
      else if (this.reason.trim().length < 5)
        this.error = "Enter a reason of at least 5 characters.";
      if (this.error) return;
      this.saving = true;
      try {
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${this.application._id}/programme-offering`,
          {
            programOfferingId: this.programOfferingId,
            expectedVersion: this.application.version,
            reason: this.reason.trim(),
          }
        );
        this.$emit("saved");
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to change the programme.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div class="action-backdrop" role="dialog" aria-modal="true">
    <div class="action-dialog bg-white rounded shadow-lg">
      <header class="border-bottom p-3 d-flex justify-content-between">
        <div>
          <h5 class="fw-bold mb-1">Change programme</h5>
          <small class="text-muted">{{ application.applicationNumber }}</small>
        </div>
        <button
          class="btn-close"
          type="button"
          :disabled="saving"
          @click="$emit('close')"
        ></button>
      </header>
      <div class="p-4">
        <div class="alert alert-warning small">
          <i class="bi bi-exclamation-triangle me-2"></i>The server will verify
          application state, enrolment, and successful-payment compatibility
          before applying this change.
        </div>
        <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
        <label class="form-label fw-semibold"
          >Destination programme offering *</label
        >
        <select v-model="programOfferingId" class="form-select mb-3">
          <option value="">Select programme offering</option>
          <option
            v-for="offering in availableOfferings"
            :key="offering._id"
            :value="offering._id"
          >
            {{ label(offering) }}
          </option>
        </select>
        <label class="form-label fw-semibold">Reason for change *</label>
        <textarea
          v-model.trim="reason"
          class="form-control"
          rows="3"
          maxlength="1000"
        ></textarea>
      </div>
      <footer class="border-top p-3 d-flex justify-content-end gap-2">
        <button
          class="btn btn-light"
          type="button"
          :disabled="saving"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="saving"
          @click="save"
        >
          <span
            v-if="saving"
            class="spinner-border spinner-border-sm me-1"
          ></span
          >Change programme
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.action-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(0, 0, 0, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.action-dialog {
  width: min(620px, 100%);
  max-height: 94vh;
  overflow-y: auto;
}
</style>
